import React, { useEffect } from 'react';

import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Translate, TranslateFunction } from 'react-localize-redux';
import { connect } from 'react-redux';

import { FormSection } from '@fd/ui/molecules';

import { notify, notifyError } from '../../../../../layouts/Notify/actions';
import { getContactFormTypeQueryKey, whitelabelService } from '../../whitelabel.service';
import {
  CONTACT_FORM_TYPE_OPTIONS,
  FLIPDISH_MESSAGES_VALUE,
  normalizeContactFormType,
} from './contactFormType';

type OuterProps = {
  appId: string;
  isDisabled?: boolean;
  translate: TranslateFunction;
};
type Props = OuterProps & MappedDispatch;

const ContactFormTypeSelect = ({
  appId,
  isDisabled,
  translate,
  notifySaved,
  notifyError,
}: Props) => {
  const queryClient = useQueryClient();
  const queryKey = [getContactFormTypeQueryKey, appId];

  const { data, isPending, isError } = useQuery({
    queryKey,
    queryFn: () => whitelabelService.getContactFormType(appId),
    enabled: !!appId,
  });

  useEffect(() => {
    if (isError) {
      notifyError();
    }
  }, [isError]);

  const updateContactFormType = useMutation({
    mutationFn: (contactFormType: string) =>
      whitelabelService.updateContactFormType(appId, contactFormType),
    onSuccess: (_result, contactFormType) => {
      queryClient.setQueryData(queryKey, { ContactFormType: contactFormType });
      notifySaved();
    },
    onError: () => notifyError(),
  });

  const currentValue = normalizeContactFormType(data?.ContactFormType);

  // Flipdish Messages is not offered for new selections, but must render if it is the current value
  const options = CONTACT_FORM_TYPE_OPTIONS.filter(
    (option) => option.value !== FLIPDISH_MESSAGES_VALUE || currentValue === FLIPDISH_MESSAGES_VALUE
  );

  return (
    <FormSection
      sectionTitle={translate('Contact_form_type') as string}
      isLoading={isPending}
      showDivider={false}
    >
      <TextField
        data-fd="set-contact-form-type-dropdown"
        fullWidth
        select
        variant="outlined"
        label={translate('Contact_form_type') as string}
        helperText={translate('Contact_form_type_helper') as string}
        disabled={isDisabled || isError || updateContactFormType.isPending}
        value={currentValue ?? ''}
        onChange={(event) => updateContactFormType.mutate(event.target.value)}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            <Translate id={option.label} />
          </MenuItem>
        ))}
      </TextField>
    </FormSection>
  );
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  notifyError: () =>
    dispatch(notifyError({ message: 'Error_please_try_again_later', translate: true })),
  notifySaved: () =>
    dispatch(
      notify({ message: 'Successfully_saved_changes', variant: 'success', translate: true })
    ),
});

export default connect(null, mapDispatchToProps)(ContactFormTypeSelect);
